import { useEffect, useRef, useState } from "react";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { getConfig } from "../config";
import { getUserManager } from "../auth/userManager";
import { useLockStore } from "../store/lockStore";
import { lockApi } from "../api/lockApi";
import { asError, eventBus } from "@optimyl/events";

/**
 * Manages the SignalR connection lifecycle for a single form. Connects on mount,
 * joins the form's group, and disconnects on unmount. The hook is responsible
 * only for the transport — domain event handlers register through the returned
 * connection in {@link useLockManager}.
 *
 * Returns the active HubConnection (null while connecting / disconnected).
 * Reflects connection state into `useLockStore.signalRConnected` so UI components
 * can show a "reconnecting" indicator if it drops.
 */
export function useSignalR(formId: string | null): HubConnection | null {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const startedRef = useRef(false);
  const setSignalRConnected = useLockStore((s) => s.setSignalRConnected);

  useEffect(() => {
    if (!formId || startedRef.current) return;
    startedRef.current = true;

    const config = getConfig();
    const negotiateUrl = `${config.API_BASE_URL}/employer-intake/lock/formLock`;
    let active = true;
    let conn: HubConnection | null = null;

    const tokenFactory = async (): Promise<string> => {
      if (!config.AUTH_ENABLED) return "";
      const user = await getUserManager().getUser();
      return user?.access_token ?? "";
    };

    (async () => {
      conn = new HubConnectionBuilder()
        .withUrl(negotiateUrl, {
          accessTokenFactory: tokenFactory,
          // Bearer-token auth — no cookies in play. withCredentials defaults to true
          // and forces the browser to expect Access-Control-Allow-Credentials on every
          // negotiate response (including CORS preflight). Functions Core Tools'
          // --cors flag does not set that header, so locally the preflight fails.
          // Disabling matches the SignalR-on-Functions guidance for token auth.
          withCredentials: false,
        })
        .withAutomaticReconnect([0, 1000, 2000, 4000, 8000, 16000])
        .configureLogging(LogLevel.Information)
        .build();

      conn.onreconnecting(() => {
        setSignalRConnected(false);
      });
      conn.onreconnected(async () => {
        setSignalRConnected(true);
        if (conn?.connectionId) {
          await lockApi.joinGroup(formId, conn.connectionId).catch((err) => {
            eventBus.publish("error", { error: asError(err), context: "useSignalR.rejoinGroup" });
          });
        }
      });
      conn.onclose(() => {
        setSignalRConnected(false);
      });

      try {
        await conn.start();
        if (!active) {
          await conn.stop();
          return;
        }
        setSignalRConnected(true);
        if (conn.connectionId) {
          await lockApi.joinGroup(formId, conn.connectionId);
        }
        setConnection(conn);
      } catch (err) {
        eventBus.publish("error", { error: asError(err), context: "useSignalR.start" });
        setSignalRConnected(false);
      }
    })();

    return () => {
      active = false;
      startedRef.current = false;
      setSignalRConnected(false);
      if (conn && conn.state !== HubConnectionState.Disconnected) {
        // Fire-and-forget stop — we don't await teardown on unmount.
        conn.stop().catch(() => { /* nothing useful to do during teardown */ });
      }
      setConnection(null);
    };
  }, [formId, setSignalRConnected]);

  return connection;
}
